// 样式包组件的 React 写法：结构照 console/design/samples/_build/pages_catalog.py，样子全在 console.css 里，
// 这里不写颜色和尺寸。挑不到组件时照最接近的写，并在 PR 里说明（DESIGN.md §0）。

import { useEffect, useState, type ReactNode } from "react";
import { CircleAlert, CircleCheck, Info, TriangleAlert } from "lucide-react";
import { isApiError } from "../api/client";
import { isTransient, loadErrorText } from "../api/errors";
import { ReasonPanel } from "./ReasonPanel";

/** 卡片：卡片头（标题 · 数量 · 右侧说明或链接）+ 内容。内容是 card-b / tbl-wrap / items 由调用方决定 */
export function Card({ title, count, extra, right, className = "", children, id }: {
  title?: ReactNode;
  count?: ReactNode;
  /** 标题后面的标签（来源、状态） */
  extra?: ReactNode;
  right?: ReactNode;
  className?: string;
  children?: ReactNode;
  id?: string;
}) {
  return (
    <section className={`card ${className}`.trim()} id={id}>
      {title !== undefined && (
        <div className="card-h">
          <h2>{title}</h2>
          {count !== undefined && <span className="n">{count}</span>}
          {extra}
          {right && <div className="right">{right}</div>}
        </div>
      )}
      {children}
    </section>
  );
}

export function CardBody({ children, className = "" }: { children: ReactNode; className?: string }) {
  return <div className={`card-b ${className}`.trim()}>{children}</div>;
}

/** 统计块：名称 → 数值（单位小一号灰）→ 一行补充 */
export function Stat({ label, value, unit, sub }: {
  label: ReactNode;
  value: ReactNode;
  unit?: string;
  sub?: ReactNode;
}) {
  return (
    <div className="stat">
      <div className="k">{label}</div>
      <div className="v">
        {value}
        {unit && <small>{unit}</small>}
      </div>
      {sub && <div className="d">{sub}</div>}
    </div>
  );
}

const MSG_ICON = { info: Info, ok: CircleCheck, warn: TriangleAlert, bad: CircleAlert };

/** 页内提示条：写「发生了什么 + 接下来怎么办」。出错、没完成的不自动消失（DESIGN.md §4.17） */
export function Msg({ tone = "info", children, acts, className = "" }: {
  tone?: "info" | "ok" | "warn" | "bad";
  children: ReactNode;
  acts?: ReactNode;
  className?: string;
}) {
  const Icon = MSG_ICON[tone];
  return (
    <div className={`msg msg-${tone} ${className}`.trim()} role={tone === "bad" ? "alert" : undefined}>
      <Icon className="ico" aria-hidden="true" />
      <div>{children}</div>
      {acts && <div className="acts">{acts}</div>}
    </div>
  );
}

/**
 * 取数失败：一句「发生了什么 + 怎么办」（文案按错误码，见 api/errors.ts），服务器 / 网络这一类给「重试」。
 * `what` 是这块数据的名字（「设备列表」）。
 * 设备内容要先选查看理由（`reason_required`）时不是出错：在这个位置换成选理由的表单（ReasonPanel）；
 * `inCard` 说明这里已经在一张卡片里，表单就不再自己套卡片。
 */
export function LoadError({ error, what, endpoint, unavailable, onRetry, className, inCard }: {
  error: unknown;
  what: string;
  endpoint?: string;
  unavailable?: string;
  onRetry?: () => void;
  className?: string;
  inCard?: boolean;
}) {
  if (isApiError(error) && error.code === "reason_required") return <ReasonPanel error={error} what={what} inCard={inCard} />;
  return (
    <Msg
      tone="bad"
      className={className}
      acts={onRetry && isTransient(error) ? <button type="button" className="btn btn-sm" onClick={onRetry}>重试</button> : undefined}
    >
      {loadErrorText(error, what, { endpoint, unavailable })}
    </Msg>
  );
}

/** 卡片里（工具条下面）的取数失败：平时是卡片体里的一条提示；要先选理由时直接换成表单，不再套一层卡片体 */
export function CardLoadError(props: Omit<Parameters<typeof LoadError>[0], "inCard">) {
  if (isApiError(props.error) && props.error.code === "reason_required") return <LoadError {...props} inCard />;
  return (
    <div className="card-b">
      <LoadError {...props} />
    </div>
  );
}

/** 空状态：一句说明这里会出现什么 / 为什么是空的 + 接下来怎么办 */
export function Empty({ title, children, acts }: {
  title: ReactNode;
  children?: ReactNode;
  acts?: ReactNode;
}) {
  return (
    <div className="empty">
      <div className="t">{title}</div>
      {children && <div className="d">{children}</div>}
      {acts && <div className="acts">{acts}</div>}
    </div>
  );
}

/** 状态标签：颜色按含义（DESIGN.md §4.11），文字本身要能读懂 */
export function St({ kind, children, title }: {
  kind?: "info" | "ok" | "warn" | "bad" | "wait" | "ai";
  children: ReactNode;
  title?: string;
}) {
  return <span className={kind ? `st st-${kind}` : "st"} title={title}>{children}</span>;
}

/** 不到 1 秒的加载不显示加载状态（DESIGN.md §3.10、NN/g 响应时间）：过了这个时间才把骨架放出来 */
export function useAfter(ms = 1000): boolean {
  const [on, setOn] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setOn(true), ms);
    return () => clearTimeout(t);
  }, [ms]);
  return on;
}

/** 骨架：几条灰条，形状大致和真内容一样 */
export function Skeleton({ lines = 3, card = true, height }: { lines?: number; card?: boolean; height?: number }) {
  const show = useAfter();
  const body = (
    <div className="card-b" style={height ? { minHeight: height } : undefined} role="status" aria-busy="true" aria-label="正在加载">
      {show &&
        Array.from({ length: lines }).map((_, i) => (
          <span key={i} className="skel" style={{ width: `${[38, 92, 76, 84, 60][i % 5]}%`, marginTop: i ? 12 : 0 }} />
        ))}
    </div>
  );
  return card ? <section className="card">{body}</section> : body;
}

/** 表尾：第 x–y 条，共 n 条 + 翻页 */
export function Pager({ page, pageSize, total, unit = "条", onPage }: {
  page: number;
  pageSize: number;
  total: number;
  unit?: string;
  onPage: (p: number) => void;
}) {
  const pages = Math.max(1, Math.ceil(total / pageSize));
  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(total, page * pageSize);
  return (
    <div className="tbl-foot">
      <span className="num">
        第 {from.toLocaleString("zh-CN")}–{to.toLocaleString("zh-CN")} {unit}，共 {total.toLocaleString("zh-CN")} {unit}
      </span>
      <div className="right">
        <button type="button" className="btn btn-sm" disabled={page <= 1} onClick={() => onPage(page - 1)}>
          上一页
        </button>
        <button type="button" className="btn btn-sm" disabled={page >= pages} onClick={() => onPage(page + 1)}>
          下一页
        </button>
      </div>
    </div>
  );
}
